import React from 'react';
import { Star, Heart, ShoppingCart, Bell, Eye } from 'lucide-react';
import OptimizedImage from './OptimizedImage';

const ProductCard = ({ product, onAddToCart, onWishlist, onQuickView, compact = false }) => {
  const {
    id,
    name,
    generic_name,
    price,
    originalPrice,
    rating,
    reviewCount,
    inStock,
    discount,
    image,
    badge,
    is_monthly_offer,
    is_best_seller,
  } = product;

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col h-full">
      {/* Image Container */}
      <div className="relative aspect-square bg-gray-50 dark:bg-gray-900 overflow-hidden flex-shrink-0">
        {/* Discount Badge */}
        {discount > 0 && (
          <span className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-rose-500 text-white text-[11px] font-bold rounded-md z-10">
            {discount}% OFF
          </span>
        )}

        {/* Feature Badge (Featured / Best Seller / Monthly Offer) */}
        {(badge || is_best_seller || is_monthly_offer) && (
          <span className="absolute top-2.5 right-2.5 px-2 py-0.5 bg-teal-600 text-white text-[11px] font-bold rounded-md z-10">
            {is_monthly_offer ? 'Offer' : is_best_seller ? 'Best Seller' : badge}
          </span>
        )}

        {/* Product Image */}
        <OptimizedImage
          src={image}
          alt={name}
          className="w-full h-full group-hover:scale-105 transition-transform duration-500"
        />

        {/* Hover overlay - clickable for QuickView */}
        <div
          className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 cursor-pointer"
          onClick={() => onQuickView?.(product)}
          data-testid={`product-image-click-${id}`}
        >
          <div className="absolute top-2.5 right-2.5 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ top: (badge || is_best_seller || is_monthly_offer) ? '2rem' : '0.625rem' }}>
            <button
              onClick={(e) => { e.stopPropagation(); onWishlist?.(id); }}
              className="p-1.5 bg-white rounded-full shadow-md hover:bg-rose-50 hover:text-rose-500 transition-colors"
            >
              <Heart className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onQuickView?.(product); }}
              className="p-1.5 bg-white rounded-full shadow-md hover:bg-teal-50 hover:text-teal-600 transition-colors"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content - flex-grow ensures uniform height */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Product Name - fixed height */}
        <h3 className="font-medium text-gray-800 dark:text-white text-sm line-clamp-2 min-h-[2.5rem] group-hover:text-teal-600 transition-colors">
          {name}
        </h3>

        {/* Composition - always takes space for uniformity */}
        <p className="text-[11px] text-gray-400 line-clamp-1 h-4 mt-0.5">
          {generic_name ? <><span className="text-gray-500">Composition:</span> {generic_name}</> : '\u00A0'}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-1 mt-1.5">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`w-3 h-3 ${
                  star <= Math.floor(rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-200'
                }`}
              />
            ))}
          </div>
          <span className="text-[11px] text-gray-400">({reviewCount})</span>
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1.5 mt-1.5">
          <span className="text-lg font-bold text-gray-800 dark:text-white">${price.toFixed(2)}</span>
          {originalPrice && (
            <span className="text-xs text-gray-400 line-through">${originalPrice.toFixed(2)}</span>
          )}
        </div>

        {/* Stock Status */}
        <p className={`text-xs font-medium mt-0.5 ${inStock ? 'text-emerald-600' : 'text-rose-500'}`}>
          {inStock ? 'In Stock' : 'Out of Stock'}
        </p>

        {/* Spacer to push button to bottom */}
        <div className="flex-grow min-h-[0.5rem]" />

        {/* Action Button - always at bottom */}
        {inStock ? (
          <button
            onClick={() => onAddToCart?.(product)}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors mt-2"
            data-testid={`add-to-cart-${id}`}
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            Add to Cart
          </button>
        ) : (
          <button
            onClick={() => onWishlist?.(id)}
            className="w-full flex items-center justify-center gap-1.5 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors mt-2"
          >
            <Bell className="w-3.5 h-3.5" />
            Notify Me
          </button>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
